import MFAccordion from '../../ui-components/MFAccordion'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { useEffect, useMemo, useRef, useState } from 'react'
import Tabs from '../../ui-components/Tabs'
import Holdings from './Holdings'
import { fetchHoldingsData } from '../../../repository/MutualFundDetail'
import { useQuery } from '@tanstack/react-query'
import { HoldingsDataType, HoldingsDistribution } from '../../../types/mutual-fund-detail.types'
import HoldingsSheet from './HoldingsSheet'
import Button from '../../ui-components/Button'
import { opacityElementByAttribute } from '../../../utils/utils'
import { trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import { getNumberInParantheses } from '../../../utils/mutualFundDetail'
import { AccordionUnavailable } from '../../error-states/AccordionUnavailable'
import { AccordionSkeleton } from '../../skeletons/AccordionSkeleton'
import { getBottomPadding } from '../../../utils/sessionStorage'
import {
  HOLDINGS_VIEW_ALL_HEADING_ID,
  HOLDINGS_VIEW_ALL_TAB_PANEL_ID
} from './holdingsViewAllConstants'

enum HoldingsTabs {
  HOLDINGS = 'Holdings',
  SECTORS = 'Sectors'
}

const HOLDINGS_SCREEN_NAME = `MF_L2_Fund_Holdings`

export default function HoldingsSection() {
  const { data: metaData, isin } = useMetaDataContext()
  const [holdingsTabData, setHoldingsTabData] = useState<HoldingsDistribution[]>([])
  const [isHoldingsAccordionOpen, setIsHoldingsAccordionOpen] = useState(false)
  const [selectedTabId, setSelectedTabId] = useState<`${HoldingsTabs}`>()
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const viewAllButtonRef = useRef<HTMLButtonElement>(null)
  const sheetHeadingRef = useRef<HTMLHeadingElement>(null)

  const {
    data,
    isLoading,
    isError: isQueryError
  } = useQuery<any, Error>({
    queryKey: ['holdingsData', isin],
    queryFn: () => {
      return fetchHoldingsData(isin)
    },
    retry: 1,
    refetchOnWindowFocus: false
  })

  const holdingsData = useMemo(() => {
    return (data?.data as HoldingsDataType) ?? null
  }, [data])

  const holdingsTabs = [
    { id: `${HoldingsTabs.HOLDINGS}`, label: `${HoldingsTabs.HOLDINGS}` },
    { id: `${HoldingsTabs.SECTORS}`, label: `${HoldingsTabs.SECTORS}` }
  ]

  useEffect(() => {
    setSelectedTabId(() =>
      holdingsData?.investmentDistribution?.length > 0 ? `${HoldingsTabs.HOLDINGS}` : HoldingsTabs.SECTORS
    )
  }, [holdingsData])

  const getHoldingsTabData = (tab: `${HoldingsTabs}`) => {
    if (data) {
      const dataCopy = data?.data as HoldingsDataType
      if (tab === HoldingsTabs.HOLDINGS)
        return dataCopy?.investmentDistribution?.sort((a, b) => (b?.weightage ?? 0) - (a?.weightage ?? 0))
      else if (tab === HoldingsTabs.SECTORS)
        return dataCopy?.sectorDistribution?.sort((a, b) => (b?.weightage ?? 0) - (a?.weightage ?? 0))
      return []
    }
    return []
  }

  useEffect(() => {
    if (holdingsData && selectedTabId) {
      setHoldingsTabData(() => getHoldingsTabData(selectedTabId))
    }
  }, [holdingsData, selectedTabId])

  const openBottomSheet = () => {
    setIsSheetOpen(true)
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        eventId: `${selectedTabId}`,
        buttonLabel: selectedTabId === HoldingsTabs.HOLDINGS ? 'more_holdings' : 'more_sectors',
        ScreenName: HOLDINGS_SCREEN_NAME,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '1')
  }

  const closeBottomSheet = () => {
    setIsSheetOpen(false)
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        eventId: `${selectedTabId}`,
        buttonLabel: 'Close',
        ScreenName: HOLDINGS_SCREEN_NAME,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '0')
    window.setTimeout(() => viewAllButtonRef.current?.focus(), 400)
  }

  const selectedTabLabel = selectedTabId ?? HoldingsTabs.HOLDINGS
  const listLabel = selectedTabLabel === HoldingsTabs.HOLDINGS ? 'Holdings' : 'Sectors'

  const isError = useMemo(() => {
    return (
      isQueryError ||
      !(holdingsData?.investmentDistribution?.length > 0 || holdingsData?.sectorDistribution?.length > 0)
    )
  }, [isQueryError, holdingsData])

  if (isLoading)
    return (
      <div>
        <AccordionSkeleton accordionUnavailableClassName='px-5 py-4' />
      </div>
    )

  if (isError)
    return (
      <div>
        <AccordionUnavailable
          imgSrc='/images/mf-l2/holdings-unavailable.svg'
          title='Holdings'
          accordionUnavailableClassName='px-5 py-4'
        />
      </div>
    )

  return (
    <div className='bg-white'>
      <MFAccordion
        icon={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/holdings-icon.svg`}
        title={`Holdings ${metaData?.fundHoldingsCount ? getNumberInParantheses(metaData?.fundHoldingsCount) : ''}`}
        isExpanded={isHoldingsAccordionOpen}
        onAccordionClick={() =>
          setIsHoldingsAccordionOpen((isHoldingsAccordionOpenPrev) => {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                eventId: 'Fund_Holdings',
                responseName: isHoldingsAccordionOpenPrev ? 'Collapse' : 'Expand',
                screenType: 'MF_L2',
                fundName: isin
              }
            })
            return !isHoldingsAccordionOpenPrev
          })
        }
        isBorderTop={true}
      >
        <div className='flex flex-col pt-2 pb-6'>
          <div aria-hidden={isSheetOpen ? true : undefined}>
            <>
              <div className='pb-4'>
                <div className='w-full h-10'>
                  <Tabs
                    selectedTabId={selectedTabId ?? ''}
                    tabsData={holdingsTabs}
                    tabButtonIdPrefix='holdings-tab'
                    onTabClick={(id) => {
                      trackEvent({
                        eventName: EventNames.UserClicked,
                        payload: {
                          eventId: 'Fund_Holdings',
                          responseName: `${id}`,
                          ScreenName: HOLDINGS_SCREEN_NAME,
                          fundName: isin,
                          screenType: 'MF_L2'
                        }
                      })
                      setSelectedTabId(() => id as `${HoldingsTabs}`)
                    }}
                    isFullWidth
                    tabsClassName='h-[40px]'
                  />
                </div>
              </div>
              {holdingsTabData?.length > 0 ? (
                <Holdings data={holdingsTabData} isShort={true} listLabel={listLabel} />
              ) : (
                <div className='w-full flex justify-center items-center h-[258px] text-[14px] text-[#6A6E6E]'>
                  {selectedTabId} data is unavailable{' '}
                </div>
              )}
              {holdingsTabData?.length > 5 && (
                <div className='pt-4 w-full'>
                  <Button
                    ref={viewAllButtonRef}
                    isFullWidth
                    onClick={openBottomSheet}
                    buttonClassName='h-[42px]'
                    aria-label={`View all ${listLabel.toLowerCase()}, ${holdingsTabData?.length} items`}
                  >
                    <div className='text-black text-13'>View All {getNumberInParantheses(holdingsTabData?.length)}</div>
                  </Button>
                </div>
              )}
            </>
          </div>

          <div>
            <HoldingsSheet
              isOpen={isSheetOpen}
              onClose={closeBottomSheet}
              titleId={HOLDINGS_VIEW_ALL_HEADING_ID}
              initialFocusRef={sheetHeadingRef}
              header={
                <div className='flex flex-col gap-4'>
                  <div className='flex items-center gap-[8px] py-1 text-[14px] text-[#232323] font-[500]'>
                    <div className='h-10 w-10 px-1' aria-hidden='true'>
                      <img
                        src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/holdings-icon.svg`}
                        alt=''
                        aria-hidden='true'
                        className='h-full w-full'
                      />
                    </div>
                    <h2
                      id={HOLDINGS_VIEW_ALL_HEADING_ID}
                      ref={sheetHeadingRef}
                      tabIndex={-1}
                      className='m-0 text-[14px] font-[500] outline-none'
                    >
                      {`Holdings ${getNumberInParantheses(metaData?.fundHoldingsCount)}`}
                    </h2>
                  </div>
                  {holdingsTabs?.length > 1 && (
                    <div className='pb-2 w-full'>
                      <div className='w-full h-10'>
                        <Tabs
                          selectedTabId={selectedTabId ?? ''}
                          tabsData={holdingsTabs}
                          tabButtonIdPrefix='holdings-tab'
                          onTabClick={(id) => {
                            trackEvent({
                              eventName: EventNames.UserClicked,
                              payload: {
                                eventId: 'Fund_Holdings',
                                responseName: `${id}`,
                                ScreenName: HOLDINGS_SCREEN_NAME,
                                fundName: isin,
                                screenType: 'MF_L2'
                              }
                            })
                            setSelectedTabId(() => id as `${HoldingsTabs}`)
                          }}
                          isFullWidth
                          tabsClassName='h-[40px]'
                        />
                      </div>
                    </div>
                  )}
                </div>
              }
              footer={
                <div style={{ paddingBottom: `${getBottomPadding() + 4}px` }} className='w-full bg-white z-[99] h-max'>
                  <Button
                    isFullWidth
                    onClick={closeBottomSheet}
                    buttonClassName='w-full bg-white z-[99] h-max px-[2px]'
                    aria-label='Close'
                  >
                    <div className='border border-[#D1D6D6] w-full rounded-xl flex items-center justify-center px-6 pt-[15px] pb-4 text-[15px] text-[#232323] font-[500]'>
                      Close
                    </div>
                  </Button>
                </div>
              }
            >
              <div className='flex flex-col gap-1 px-5'>
                <div
                  role='tabpanel'
                  id={HOLDINGS_VIEW_ALL_TAB_PANEL_ID}
                  aria-labelledby={selectedTabId ? `holdings-tab-${selectedTabId}` : undefined}
                  className='flex flex-col gap-3'
                >
                  {holdingsTabData?.length > 0 ? (
                    <Holdings data={holdingsTabData} isShort={false} listLabel={listLabel} />
                  ) : (
                    <div className='w-full flex justify-center items-center h-[258px] text-[14px] text-[#6A6E6E]'>
                      {selectedTabId} data is unavailable{' '}
                    </div>
                  )}
                </div>
              </div>
            </HoldingsSheet>
          </div>
        </div>
      </MFAccordion>
    </div>
  )
}
